import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useCopilotApprovalReload } from 'components/Copilot/useCopilotApprovalReload';
import { applyGlobalFilter, removeGlobalFilter } from 'components/GlobalFilter/GlobalFilterHelper';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { FilterProcessingActivityGroupKeys } from 'components/ProcessingActivity/FilterProcessingActivityGroupKeys';
import SceneBar from 'components/SceneBar/SceneBar';
import { getCurrentPivotKeyFromURL, IScenePivotItemProps } from 'components/SceneBar/ScenePivot';
import { globalNotFoundURL } from 'globalConstants';
import { globalStackStylesHeight100, globalStackTokensGapMedium } from 'globalStyles';
import Asset from 'models/asset/asset';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Classification from 'models/classification/classification';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import { useCopilotContext } from 'models/copilot/copilotContext';
import { CopilotGenericContent } from 'models/copilot/copilotGeneric';
import { CopilotScene } from 'models/copilot/copilotScene';
import ProcessingActivity from 'models/processingActivity';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import {
  apiAddProcessingActivity,
  apiGetProcessingActivities,
  apiRemoveProcessingActivity,
  apiUpdateProcessingActivity,
} from 'services/Api/processingActivityService';
import { apiGetStringValues } from 'services/Api/stringValueService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnCode } from 'utils/sorting';
import FilterProcessingActivities from './FilterProcessingActivities';
import TabOverview from './TabOverview';

interface IProcessingActivitiesProps extends RouteComponentProps {}
interface IProcessingActivitiesProps {}

const ProcessingActivitiesOverview = (props: IProcessingActivitiesProps) => {
  const { t } = useTranslation(['translation', 'processingActivity', 'processingActivities', 'exceptions']);
  const appContext = useContext(AppContext);
  const _sceneBaseURL = '/processingactivities';
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [processingActivities, setProcessingActivities] = useState<ProcessingActivity[]>([]);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string[]>([]);
  const [dataSubjectScales, setDataSubjectScales] = useState<StringValue[]>([]);
  const copilotContent = useMemo(() => {
    const content = new CopilotGenericContent();
    content.scene = CopilotScene.ProcessingActivities;

    return content;
  }, []);
  useCopilotContext(appContext.copilotBridge, CopilotPanelContext.Generic, copilotContent);

  // Unique data assets, classifications and data subject scales for filter options
  const dataAssets: Asset[] = [
    ...new Map(processingActivities.flatMap((pa) => pa.data).map((da) => [da.assetId, da])).values(),
  ];
  const classifications: Classification[] = [
    ...new Map(dataAssets.flatMap((a) => a.classifications ?? []).map((c) => [c.classificationId, c])).values(),
  ];

  const getPivotItems = () => {
    const pivotItems: IScenePivotItemProps[] = [
      { key: 'overview', text: t('processingActivities:Pivot.Overview'), url: '/overview' },
    ];

    return pivotItems;
  };

  const toggleFilterDialog = (state: boolean) => {
    setShowFilterDialog(state);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newProcessingActivities = await apiGetProcessingActivities(accessToken, appContext.globalDataCache);
      const _dataSubjectScales = await apiGetStringValues(StringValueTypes.DataSubjectScale, accessToken);

      newProcessingActivities.sort((a, b) => sortOnCode(a.code, b.code));

      setProcessingActivities(newProcessingActivities);
      setDataSubjectScales(_dataSubjectScales);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  useCopilotApprovalReload(loadData);

  const updateSelectedFilter = (filter: string[]) => {
    saveFilterToStorage(filter);
    setSelectedFilter(filter);
  };

  const setFilterFromURLParams = () => {
    //const queryParams = new URLSearchParams(window.location.search);
    const filters: string[] = [];

    if (filters.length > 0) {
      setSelectedFilter(filters);
    }
  };

  useEffect(() => {
    loadData();
    setFilterFromStorage();
    setFilterFromURLParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilterFromStorage = () => {
    const filterString = getLocalStorageData(appContext, LocalStorageKeys.SceneFilterProcessingActivity);
    let filter = filterString?.split(',');
    filter = applyGlobalFilter(appContext, filter, FilterProcessingActivityGroupKeys);
    setSelectedFilter(filter);
  };

  const saveFilterToStorage = (filter: string[]) => {
    const filterToSave = removeGlobalFilter(
      appContext,
      filter,
      FilterProcessingActivityGroupKeys,
      LocalStorageKeys.SceneFilterProcessingActivity,
    );
    setLocalStorageData(appContext, LocalStorageKeys.SceneFilterProcessingActivity, filterToSave.join(','));
  };

  const addProcessingActivity = async (processingActivity: ProcessingActivity, open: boolean) => {
    if (isLoading || isUpdating) return;

    try {
      setIsUpdating(true);
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const _processingActivity: ProcessingActivity = await apiAddProcessingActivity(
        processingActivity,
        accessToken,
        appContext.globalDataCache,
      );

      setProcessingActivities([...processingActivities, _processingActivity]);

      if (open) {
        const url = `/processingActivity/${_processingActivity.processingActivityId}`;
        props.history.push(url);
      }
    } catch (err) {
      if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_ProcessingActivity_DuplicateCode) {
        appContext.showNotification(
          t('exceptions:ProcessingActivity.DuplicateCode', { code: processingActivity.code }),
          true,
        );
      } else {
        appContext.setError(err);
      }
    } finally {
      appContext.hideContentLoader();
      setIsUpdating(false);
    }
  };

  const editProcessingActivity = async (processingActivity: ProcessingActivity) => {
    if (isLoading || isUpdating) return;
    try {
      setIsUpdating(true);

      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const updatedProcessingActivity = await apiUpdateProcessingActivity(
        processingActivity,
        accessToken,
        appContext.globalDataCache,
      );

      updateProcessingActivities([updatedProcessingActivity]);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsUpdating(false);
    }
  };

  const updateProcessingActivities = (updatedProcessingActivities: ProcessingActivity[]) => {
    let newProcessingActivities = [...processingActivities];
    for (let idx = 0; idx < updatedProcessingActivities.length; idx++) {
      const updatedProcessingActivity = updatedProcessingActivities[idx];
      newProcessingActivities = newProcessingActivities.map((_processingActivity) =>
        _processingActivity.processingActivityId !== updatedProcessingActivity.processingActivityId
          ? _processingActivity
          : updatedProcessingActivity,
      );
    }
    setProcessingActivities(newProcessingActivities);
  };

  const removeProcessingActivities = async (processingActivitiesToDelete: ProcessingActivity[]) => {
    if (isLoading || isUpdating || processingActivities.length === 0) return;

    try {
      setIsUpdating(true);
      appContext.showContentLoader();

      let newProcessingActivities = [...processingActivities];
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      for (let i = 0; i < processingActivitiesToDelete.length; i++) {
        const processingActivity = processingActivitiesToDelete[i];
        if (hasUserDataPermission(appContext, [processingActivity.authSchemaId], AuthSchemaLineOperation.Delete)) {
          await apiRemoveProcessingActivity(processingActivity, accessToken);
          newProcessingActivities = newProcessingActivities.filter(
            (r) => r.processingActivityId !== processingActivity.processingActivityId,
          );
        }
      }

      setProcessingActivities(newProcessingActivities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsUpdating(false);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack horizontal styles={globalStackStylesHeight100}>
      {isUpdating && <OverlayLoader text={t('translation:General.Notifications.Updating')} />}
      <IPStack.Item grow>
        <IPStack verticalFill tokens={globalStackTokensGapMedium}>
          <IPStack.Item>
            <SceneBar
              title={t('processingActivities:Title')}
              subtitle={t('processingActivities:SubTitle')}
              image="processing-activities.png"
              pivotItems={getPivotItems()}
              selectedPivotKey={getCurrentPivotKeyFromURL(getPivotItems())}
            >
              <FilterProcessingActivities
                toggleDialog={toggleFilterDialog}
                isOpen={showFilterDialog}
                updateSelectedFilter={updateSelectedFilter}
                selectedFilter={selectedFilter}
                dataAssets={dataAssets}
                classifications={classifications}
                dataSubjectScales={dataSubjectScales}
              />
            </SceneBar>
          </IPStack.Item>
          <IPStack.Item grow>
            <Switch>
              <Route
                path={`${_sceneBaseURL}/overview`}
                render={() => (
                  <TabOverview
                    isLoading={isLoading}
                    processingActivities={processingActivities}
                    updateSelectedFilter={updateSelectedFilter}
                    selectedFilter={selectedFilter}
                    editProcessingActivity={editProcessingActivity}
                    removeProcessingActivities={removeProcessingActivities}
                    addProcessingActivity={addProcessingActivity}
                    updateProcessingActivities={updateProcessingActivities}
                  />
                )}
              />
              <Redirect from={_sceneBaseURL} to={`${_sceneBaseURL}/overview`}></Redirect>
              <Redirect to={globalNotFoundURL}></Redirect>
            </Switch>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default ProcessingActivitiesOverview;
